/**
 * Main layout wrapper component
 * Provides consistent page structure with header, content area, and footer
 * Ensures footer sticks to bottom with min-height screen
 *
 * @module Layout
 */

import { ReactNode } from 'react'
import { logger } from '../../utils/logger'
import { Header } from './Header'
import { Footer } from './Footer'

/**
 * Layout component props
 */
interface LayoutProps {
  /**
   * Page content to render between header and footer
   */
  children: ReactNode

  /**
   * Optional CSS class name for the main content area
   */
  contentClassName?: string

  /**
   * Optional CSS class name for the root layout element
   */
  className?: string
}

/**
 * Main layout component
 * Wraps pages with header, main content area, and footer
 * Uses flexbox to ensure footer stays at bottom
 */
export function Layout({
  children,
  contentClassName = '',
  className = '',
}: LayoutProps) {
  logger.debug('[WALRUS] Layout: rendering', {
    hasChildren: !!children,
    contentClassName,
  })

  return (
    <div
      className={`flex flex-col min-h-screen bg-walrus-black text-walrus-white ${className}`}
      role="document"
    >
      {/* Header Navigation */}
      <Header />

      {/* Main Content Area */}
      <main className={`flex-1 w-full ${contentClassName}`}>
        {children}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}
